import React from 'react';
import { motion } from 'framer-motion';
import {
  FaCode,
  FaReact,
  FaPython,
  FaDatabase as FaSQL,
  FaNetworkWired,
  FaTerminal,
  FaUserShield
} from 'react-icons/fa';
import { SiFlutter, SiCisco, SiKalilinux, SiLinux } from 'react-icons/si';
import { MdSecurity, MdMonitorHeart } from 'react-icons/md';
import './About.css';
import myPhoto from '../../assets/MyPhoto.jpeg';

const About = () => {
  const skills = [
    { name: 'Cisco Packet Tracer', level: 85, icon: <SiCisco /> },
    { name: 'Nmap & Recon', level: 80, icon: <SiKalilinux /> },
    { name: 'Wazuh SIEM', level: 75, icon: <MdSecurity /> },
    { name: 'LibreNMS & Zabbix', level: 75, icon: <MdMonitorHeart /> },
    { name: 'PuTTY / SSH & CLI', level: 85, icon: <FaTerminal /> },
    { name: 'Network Protocols & VLANs', level: 80, icon: <FaNetworkWired /> },
    { name: 'Linux System Admin', level: 75, icon: <SiLinux /> },
    { name: 'Python', level: 75, icon: <FaPython /> },
    { name: 'React / JavaScript', level: 75, icon: <FaReact /> },
    { name: 'Flutter / Dart', level: 70, icon: <SiFlutter /> },
    { name: 'SQL & Databases', level: 60, icon: <FaSQL /> },
  ];

  return (
    <section id="about" className="about section">
      <div className="container">
        <div className="section-title">
          <h2>About Me</h2>
        </div>

        <div className="about-content">
          <motion.div
            className="about-text"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p>
              I am a Computer Engineer graduated from <strong className="highlight-text">Kathmandu University</strong> and a registered professional engineer certified by the <strong className="highlight-text">Nepal Engineering Council (NEC)</strong>. My primary focus and expertise lie in <strong className="highlight-text">Network and Security Engineering</strong>—architecting resilient network infrastructures, hardening enterprise perimeters, and ensuring continuous system reliability.
            </p>
            <p>
              My hands-on technical proficiencies include designing and simulating scalable topologies in <strong>Cisco Packet Tracer</strong>, executing network reconnaissance and vulnerability assessments with <strong>Nmap</strong>, managing security events and intrusion detection via <strong>Wazuh SIEM</strong>, and maintaining high availability through real-time SNMP monitoring with <strong>LibreNMS</strong> and <strong>Zabbix</strong> over secure SSH/PuTTY environments.
            </p>
            <p>
              Additionally, I utilize Python and software engineering principles to build automated security scripts, network tooling, and supporting digital systems.
            </p>
          </motion.div>

          <motion.div
            className="about-image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img src={myPhoto} alt="Aryan Khanal" />
          </motion.div>
        </div>

        <motion.div
          className="skills-container"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="section-subtitle">Technical Skills & Tools</h3>
          <div className="skills-grid">
            {skills.map((skill, index) => (
              <motion.div
                className="skill-card glass"
                key={index}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="skill-info">
                  <div className="skill-name">
                    <span className="skill-icon">{skill.icon}</span>
                    {skill.name}
                  </div>
                  <span className="skill-percentage">{skill.level}%</span>
                </div>
                <div className="skill-bar">
                  <motion.div
                    className="skill-level"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${skill.level}%` }}
                    transition={{ duration: 1, delay: index * 0.08 }}
                    viewport={{ once: true }}
                  ></motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="services"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="section-subtitle">My Expertise</h3>
          <div className="services-grid">
            <motion.div
              className="service-card glass"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <div className="service-icon">
                <FaNetworkWired />
              </div>
              <h4>Network Architecture & Engineering</h4>
              <p>Designing enterprise topologies, VLAN segmentation, subnetting, OSPF/RIP routing, and switch/router configuration in Cisco Packet Tracer.</p>
            </motion.div>

            <motion.div
              className="service-card glass"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <div className="service-icon">
                <FaUserShield />
              </div>
              <h4>Cybersecurity & SIEM Operations</h4>
              <p>Intrusion detection, security log correlation with Wazuh SIEM, network vulnerability reconnaissance using Nmap, and firewall access controls.</p>
            </motion.div>

            <motion.div
              className="service-card glass"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <div className="service-icon">
                <MdMonitorHeart />
              </div>
              <h4>Network Monitoring & Systems</h4>
              <p>Real-time infrastructure health tracking, SNMP metrics, alert automations using LibreNMS and Zabbix, managed via PuTTY and secure SSH.</p>
            </motion.div>

            <motion.div
              className="service-card glass"
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <div className="service-icon">
                <FaCode />
              </div>
              <h4>Web & App Development</h4>
              <p>Engineering responsive web interfaces with React and cross-platform mobile apps using Flutter/Dart with secure backend API integrations.</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;